/**
 * @module routes.Sessions.AllCoachesPublicSession._components.facilityCalendarGrid
 *
 * Derivations behind the Facility Calendar tab (frame 526:29178): one column per
 * coach, one row per start time, an hour label down the left gutter, and a day
 * stepper that only lands on days someone can be booked.
 */
import { DateTime } from "luxon";

import { AvailableSlotModel } from "app/models/scheduling/AvailableSlotModel";
import { CalendarPreferences } from "app/models/CalendarPreferences";
import { PerCoachSlots } from "./useFacilityAvailability";

/**
 * One slot per coach per interval, keeping the earliest in each.
 *
 * Coaches are not on a common interval: availability is asked per booking option,
 * so a coach whose default option is 15 minutes offers 8:00, 8:15, 8:30 while one
 * on 30 offers 8:00, 8:30. Rows are the union of those times, so mixing them left
 * a ragged lattice where most cells in the quarter-hour rows were blank.
 *
 * Snapping to one interval — an hour unless a duration is chosen — gives every
 * column the same rows. The time booked is still a real available slot: the
 * earliest one inside the interval, not the interval's own boundary.
 */
export const snapSlotsToInterval = (
  slotsByCoach: PerCoachSlots[],
  intervalMinutes: number,
): PerCoachSlots[] => {
  if (!intervalMinutes || intervalMinutes <= 0) {
    return slotsByCoach || [];
  }

  return (slotsByCoach || []).map(({ coachId, slots }) => {
    const earliestPerBucket = new Map<number, AvailableSlotModel>();

    [...(slots || [])]
      .sort((a, b) => a.timeId - b.timeId)
      .forEach((slot) => {
        const minutesIntoDay =
          slot.startHour.hour * 60 + slot.startHour.minute;
        const bucket = Math.floor(minutesIntoDay / intervalMinutes);
        if (!earliestPerBucket.has(bucket)) {
          earliestPerBucket.set(bucket, slot);
        }
      });

    return { coachId, slots: Array.from(earliestPerBucket.values()) };
  });
};

/** One row of the grid: a start time, plus the hour label when it opens an hour. */
export interface GridRow {
  timeId: number;
  startHour: DateTime;
  /** "7 AM" on the hour, otherwise null — the gutter labels hours, not slots. */
  hourLabel: string | null;
}

/**
 * The gutter's hour, in the reader's clock (CFTB-3431): "7 AM" on a 12-hour
 * setting, "07:00" on 24-hour. Only whole hours are labelled; the rows between
 * them carry the grid's finer intervals and stay blank.
 */
export const hourLabel = (
  start: DateTime,
  timeFormat?: CalendarPreferences["timeFormat"],
): string | null => {
  if (start.minute !== 0) {
    return null;
  }

  return timeFormat === "24h"
    ? start.toFormat("HH:mm")
    : start.toFormat("h a").toUpperCase();
};

/**
 * The grid's rows, built from the start times that actually exist rather than a
 * fixed half-hour lattice. Coaches can be configured to any interval (and the
 * facility's `schedulingInterval` is a setting), so a hardcoded lattice would
 * either invent rows nobody offers or drop slots that fall between them.
 */
export const gridRows = (
  slotsByCoach: PerCoachSlots[],
  timeFormat?: CalendarPreferences["timeFormat"],
): GridRow[] => {
  const byTime = new Map<number, DateTime>();
  (slotsByCoach || []).forEach(({ slots }) => {
    (slots || []).forEach((slot) => {
      if (!byTime.has(slot.timeId)) {
        byTime.set(slot.timeId, slot.startHour);
      }
    });
  });

  return Array.from(byTime.entries())
    .sort(([a], [b]) => a - b)
    .map(([timeId, startHour]) => ({
      timeId,
      startHour,
      hourLabel: hourLabel(startHour, timeFormat),
    }));
};

/**
 * The slot in a given cell, or null for the empty cells the frame shows where a
 * coach has no availability at that time.
 */
export const slotAt = (
  slotsByCoach: PerCoachSlots[],
  coachId: string,
  timeId: number,
): AvailableSlotModel | null => {
  const entry = (slotsByCoach || []).find((row) => row.coachId === coachId);
  return (entry?.slots || []).find((slot) => slot.timeId === timeId) || null;
};

/** Coaches with at least one slot on the day — the columns worth drawing. */
export const coachIdsWithSlots = (slotsByCoach: PerCoachSlots[]): string[] =>
  (slotsByCoach || [])
    .filter(({ slots }) => (slots || []).length > 0)
    .map(({ coachId }) => coachId);

/**
 * The day the `‹` / `›` stepper moves to: the nearest available day in that
 * direction, or null at either end. Comment #30 offers stepping between
 * available days as one of the navigation options, and stepping onto an empty
 * day would be a dead press.
 */
export const stepDay = (
  availableDates: Set<string>,
  current: string | null,
  direction: 1 | -1,
): string | null => {
  if (!current) {
    return null;
  }
  const ordered = Array.from(availableDates).sort();
  const candidates =
    direction === 1
      ? ordered.filter((date) => date > current)
      : ordered.filter((date) => date < current).reverse();
  return candidates[0] ?? null;
};
